import { ColumnStore, DateHelper, DomHelper, EventHelper, Tooltip } from "@bryntum/schedulerpro";
import bgContrastHelper from "../../other/utils/bgUtils.jsx";
import { getSetting } from "../../react/context/bryntumContext.jsx";
import {applyStripedBackground, defaultGroupRenderer} from "../../other/utils/utils";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import {MIXED_TASK_COLOR, MIXED_TASK_STYLE, STRIPED_TASK_STYLE} from "../../other/constants/TaskTypes.jsx";
import { SWIVU_TOOLTIP_TRIGGER_CLS, TASK_BUDGET_FIELDS, ViewType } from "../../other/constants/OtherKeys.jsx";
import {SWIVU_TASK_DEFAULT_COLOR} from "../../other/configs/globalConfig.jsx";
import SwivuStateColumn from "./SwivuStateColumn.jsx";
import { createTooltipRow } from "../../other/utils/otherUtils.jsx";
import { WIDGET_KEYS } from "../../other/constants/WidgetKeys.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";

export default class UnplannedTaskColumn extends SwivuStateColumn {
  static get $name() {
    return "UnplannedTaskColumn";
  }

  static get type() {
    return "unplannedTask";
  }

  static get fields() {
    return ["barMargin"];
  }

  static get defaults() {
    return {
      barMargin: 10,
      cls: "swivu-task-column",
    };
  }

  defaultRenderer({ record, grid }) {
    // Todo: Use taskRenderer from bgUtils
    const backgroundColor = `${
      record.color ? record.color : SWIVU_TASK_DEFAULT_COLOR
    }`;
    let globalColor = getSetting("taskColor", null, true);
    let globalStyle = getSetting("taskStyle", null, true);
    if (globalStyle === MIXED_TASK_STYLE) globalStyle = '';
    if (globalColor === MIXED_TASK_COLOR) globalColor = '';
    const isStripStyle = record?.taskStyle ? record.taskStyle === STRIPED_TASK_STYLE : globalStyle === STRIPED_TASK_STYLE;
    const contrastColor = bgContrastHelper(globalColor || record.color, "#fff", "#000");

    const lineStylePermitsContrast = (lineStyle) => ![ 'line', 'dashed', 'minimal', 'hollow' ].includes(lineStyle);

    const canApplyBgContrast = isStripStyle || lineStylePermitsContrast(record.taskStyle || globalStyle);

    const color = canApplyBgContrast ? (contrastColor || "#000") : '';

    const { rowHeight } = grid;
    const { barMargin } = this;
    const viewType = getSetting("viewType");
    const style = {
      flexDirection: "column",
      alignItems: "flex-start",
      whiteSpace: "inherit"
    };

    if (isStripStyle) {
      style.background = applyStripedBackground(globalColor || backgroundColor);
    } else {
      //Updating Task Color
      style.color = globalColor || record.color;
    }

    // Have the same markup as event to achieve theme-dependent consistent styling
    const content = {
      tag: "div",
      class: `${SWIVU_TOOLTIP_TRIGGER_CLS} b-sch-event-wrap b-sch-swivu-task-style ${ !isStripStyle ? `b-sch-style-${record.taskStyle || globalStyle || "plain"} b-sch-custom-color` : "swivu-time-event" } swivu-unplanned-task !relative w-full b-style-${record.taskStyle || globalStyle || "plain"}`,
      style: {
        pointerEvents: 'unset',
        '--b-primary': globalColor || backgroundColor /* Decides background color */
      },
      children: [
        {
          tag: "div",
          class: "b-sch-event b-has-content",
          style,
          children: [
            {
              tag: "div",
              class: `b-sch-event-content ${!canApplyBgContrast ? 'theme-text-color' : ''}`,
              html: record.name,
              style: {
                color,
                fontSize: "1em",
                alignItems: "center",
                flexBasis: `${(rowHeight - (viewType === ViewType.compact ? 0.5:
                  viewType === ViewType.compressed ? 0.25: 2) * barMargin)}px`,
                  ...(record.archived && { textDecoration: 'line-through' })
              }
            }
          ]
        }
      ]
    };
    return content;
  }

  groupRenderer(e) {
    return defaultGroupRenderer(e);
  }

  onLoad(project, { editorHandler, stateProvider, currentViewRef, scheduler = null, canDrag = false, user, entityManager }) {
    this.project = project;
    this.editorHandler = editorHandler;
    this.stateProvider = stateProvider;
    this.currentViewRef = currentViewRef;
    this.scheduler = scheduler;
    this.canDrag = canDrag;
    this.user = user;
    this.entityManager = entityManager;
  }

  headerRenderer({ column }) {
    if (!this.quickFindIconEl) {
      this.quickFindIconEl = DomHelper.createElement({
        tag: "div",
        parent: column.textWrapper,
        className: "swivu-quick-find fa fa-search",
      })
      new Tooltip({
        forElement: column.textWrapper,
        forSelector: ".swivu-quick-find",
        html: "Find"
      })
      this.quickFindElListener = EventHelper.on({
        element: this.quickFindIconEl,
        click: "handleQuickFindClick",
        thisObj: column
      });
    }
    return column.text;
  }

  handleQuickFindClick(event) {
    event.stopPropagation();
    this.editorHandler.openEditor(EDITOR_KEYS.taskFinderEditor, null, {
      point: [event.x, event.y],
      scheduler: this.scheduler,
      canDrag: this.canDrag,
      onTaskClick: ({ record }) => this.entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: record.id })
    }, {
      by: "recordType",
      useExisting: true
    });
  }

  tooltipRenderer({ record, event, tip, cellTooltip }) {
    const { swivuShiftDisabled } = cellTooltip;
    if (swivuShiftDisabled || event.shiftKey) {
      tip.minWidth = "35em";
      tip.cls = "swivu-task-tooltip";
      // const { budgetRecord } = record;
      let budgetRecord = null;
      if (record.budgetId) {
        budgetRecord = this.project.taskBudgetStore.getById(record.budgetId);
      }
      const budgetColumn = this.grid.columns.find((item) => item.type === WIDGET_KEYS.budgetAccountColumn);
      const budgetTooltipContent = budgetColumn.tooltipRenderer({ record }) ?? "";
      return `<div class="flex flex-col gap-4 p-1">
        <div class="swivu-task-tooltip-grid">
          ${createTooltipRow(record.name, "swivu-task-tooltip-header")}
          ${createTooltipRow(record.description, "swivu-task-tooltip-subtitle")}
          ${createTooltipRow(record.address?.formatted_address, "swivu-task-tooltip-subtitle")}
        </div>
        <div class="swivu-task-tooltip-grid">
          ${createTooltipRow(record.clientName, "swivu-task-tooltip-row", "Client")}
          ${createTooltipRow(record.statusValue, "swivu-task-tooltip-row", "Status")}
          ${createTooltipRow(record.categoryValue, "swivu-task-tooltip-row", "Category")}
          ${createTooltipRow(record.tagsValue, "swivu-task-tooltip-row", "Tags", true)}
          ${createTooltipRow(record.phaseValue, "swivu-task-tooltip-row", "Phase")}
          ${createTooltipRow(record.teamValue, "swivu-task-tooltip-row", "Team")}
          ${createTooltipRow(budgetRecord?.[TASK_BUDGET_FIELDS.budgetTitleDisplayValue], "swivu-task-tooltip-row", "Budget ID")}
        </div>
        ${record.planningDatesRecords.length || budgetTooltipContent ? `<div class="flex gap-4 pt-3 border-t border-border">
          ${record.planningDatesRecords.length ? `<div class="swivu-task-tooltip-grid flex-1">
            ${record.planningDatesRecords.map((dateRecord) => createTooltipRow(DateHelper.format(dateRecord.startDate, "M/D/YYYY hh:mm A"), "swivu-task-tooltip-row", dateRecord.name)).join("")}
          </div>` : ""}
          ${budgetTooltipContent}
        </div>` : ""}
      </div>`;
    }
  }

  onDestroy() {
    if (this.quickFindElListener) this.quickFindElListener();
  }
}

ColumnStore.registerColumnType(UnplannedTaskColumn);
